import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { ArrowSyncIcon, CloseMiniIcon, SettingsSliderHorizontalIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { colorIconActive, colorIconStandard, colorTextDestructive } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useMemo, useRef } from 'react';
import { Layout } from 'components/layout';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { ListItemActions } from '../styles';
import { useHandleApplyAndClose } from './use_handle_apply_and_close';
import { useUserPoolSidePanel } from './user_pool_side_panel/context';
import { getDefaultUserPoolName } from '../../helpers';
import { useHostsListData } from '../../hooks';
const PoolDetails = props => <Layout align="center" gap="12" {...props} />;
const TextContainer = props => <Layout align="start" direction="column" {...props} />;
const poolWrapperIsSelectedCss = "p12i9vy3";
const poolWrapperIsLockedCss = "ps8tiuy";
const poolWrapperCss = "p1tmkn96";
const PoolWrapper = forwardRef((props, ref) => {
  const {
    isSelected,
    isLocked,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(poolWrapperCss, {
    [poolWrapperIsSelectedCss]: isSelected,
    [poolWrapperIsLockedCss]: isLocked
  }, props.className)} />;
});
const clickablePoolWrapperCss = "c1fxb550";
const ClickablePoolWrapper = forwardRef((props, ref) => {
  const as = BareButton;
  return <PoolWrapper ref={ref} {...props} as={as} className={classNames(clickablePoolWrapperCss, props.className)} />;
});
const editButtonCss = "e1gwlpga";
const EditButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(editButtonCss, props.className)} />);
export const UserPoolListItem = ({
  eventType,
  userPool,
  isLastUserPool,
  isLocked,
  onRemove
}) => {
  const targetRef = useRef(null);
  const handleApplyAndClose = useHandleApplyAndClose({
    eventType,
    isLocked
  });
  const {
    isSubmitting
  } = useFormContext();
  const {
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const {
    isTeam
  } = eventType;
  const {
    isOpen,
    userPoolUid: sidePanelUserPoolUid,
    openUserPoolSidePanel
  } = useUserPoolSidePanel();
  const {
    userPoolUid,
    isHostsEmpty,
    hosts,
    nameError
  } = useHostsListData(userPool, isTeam);
  const canDelete = isCollectiveRoundRobin && !isTeam && !isLastUserPool && !isLocked && !!onRemove;
  const isSelected = isOpen && (!sidePanelUserPoolUid || sidePanelUserPoolUid === userPoolUid);
  const handleSelect = () => {
    if (isSelected) {
      handleApplyAndClose();
    } else {
      openUserPoolSidePanel({
        userPoolUid,
        targetRef,
        minHeight: isHostsEmpty ? '800px' : ''
      });
    }
  };
  const handleItemClick = event => {
    event.stopPropagation();
    handleSelect();
  };
  const handleRemoveClick = event => {
    event.stopPropagation();
    if (onRemove && userPool) {
      onRemove(userPoolUid);
    }
  };
  const userPoolName = userPool?.name || getDefaultUserPoolName(userPool?.position);
  const userPoolSummary = useMemo(() => {
    if (isHostsEmpty) {
      return isSelected ? I18n.t('publisher.event_type_editor.sections.hosts.round_robin.empty_pool_summary') : I18n.t('publisher.event_type_editor.sections.hosts.round_robin.error_pool_summary');
    }
    if (nameError) {
      return nameError;
    }
    return I18n.t('publisher.event_type_editor.sections.hosts.round_robin.pool_summary', {
      count: hosts.length
    });
  }, [hosts.length, isHostsEmpty, isSelected, nameError]);
  const hasError = isHostsEmpty && !isSelected || nameError;
  const PoolWrapperComponent = isLocked ? ClickablePoolWrapper : PoolWrapper;
  return <PoolWrapperComponent ref={targetRef} isLocked={isLocked} isSelected={isSelected} onClick={isLocked ? handleItemClick : null} aria-label={isLocked ? userPoolName : null}>
      <PoolDetails>
        <ArrowSyncIcon size="large" />
        <TextContainer>
          <BodyText weight="semibold">{userPoolName}</BodyText>
          <BodyText size="small" color={hasError ? colorTextDestructive : 'muted'}>{userPoolSummary}</BodyText>
        </TextContainer>
      </PoolDetails>
      {!isLocked && <ListItemActions>
          <EditButton disabled={isSubmitting} onClick={handleItemClick} size="small" title={`${I18n.t('publisher.action.edit')} ${userPoolName}`}>
            <SettingsSliderHorizontalIcon color={isSelected ? colorIconActive : colorIconStandard} />
          </EditButton>
          {canDelete && <IconButton title={`${I18n.t('publisher.action.delete')} ${userPoolName}`} disabled={isSubmitting} onClick={handleRemoveClick} size="small">
              <CloseMiniIcon />
            </IconButton>}
        </ListItemActions>}
    </PoolWrapperComponent>;
};

require("./user_pool_list_item.wyw-in-js.css!=!../../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./user_pool_list_item.js");